import { Link } from 'react-router-dom';
import styles from './Button.module.css';

interface ButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'secondaryLight' | 'cta';
  size?: 'small' | 'medium' | 'large';
  href?: string;
  onClick?: () => void;
  type?: 'button' | 'submit';
  showArrow?: boolean;
  className?: string;
  disabled?: boolean;
}

export const Button = ({
  children,
  variant = 'primary',
  size = 'medium',
  href,
  onClick,
  type = 'button',
  showArrow = false,
  className = '',
  disabled = false,
}: ButtonProps) => {
  const classes = [
    styles.button,
    styles[variant],
    size !== 'medium' ? styles[size] : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (href) {
    const isExternal = href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:');
    
    if (isExternal) {
      return (
        <a href={href} className={classes} onClick={onClick}>
          {children}
          {showArrow && (
            <svg className={styles.arrow} width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          )}
        </a>
      );
    }

    return (
      <Link
        to={href}
        className={classes}
        onClick={(e) => {
          onClick?.();
          // Handle same-page hash scroll manually (Safari-safe)
          if (href.includes('#')) {
            const hash = href.split('#')[1];
            e.preventDefault();
            window.history.replaceState(null, '', href);
            // Small delay for Safari iOS to settle
            setTimeout(() => {
              const el = document.getElementById(hash);
              if (el) {
                const navHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--nav-height')) * 16 || 64;
                const y = el.getBoundingClientRect().top + window.scrollY - navHeight - 16;
                window.scrollTo({ top: y, behavior: 'smooth' });
              }
            }, 50);
          }
        }}
      >
        {children}
        {showArrow && (
          <svg className={styles.arrow} width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        )}
      </Link>
    );
  }

  return (
    <button type={type} className={classes} onClick={onClick} disabled={disabled}>
      {children}
      {showArrow && (
        <svg className={styles.arrow} width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
        </svg>
      )}
    </button>
  );
};
